// Admin "Categories & attributes" — route /categories (not "/admin/..." —
// the dev-proxy/Nginx prefix collision noted across App.jsx's route table).
//
// Two columns: the category tree on the left (select one to work on it),
// its detail + attributes on the right. Everything the brief asks for:
// create a category, rename it in both languages, reorder (sort_order), move
// it in the tree (parent, including back to top level), deactivate; and the
// same set for its attributes (label, input type, select options, order,
// required, deactivate).
//
// Deletion rule (root CLAUDE.md): anything in use is DEACTIVATED, never
// hard-deleted. Delete is offered only as a fallback for a genuinely empty
// category and the backend enforces that (CATEGORY_HAS_DEPENDENTS → a toast
// telling the admin to deactivate instead).
//
// A third section, ProductBrandsSection, lives below the tree+detail layout
// and manages the flat, admin-wide `product_brands` list (migration 027/
// 028) that backs ProductForm's brand dropdown. It is deliberately NOT part
// of CategoryDetail/AttributesEditor — brands aren't scoped to a category —
// so it renders unconditionally, regardless of which (if any) category is
// selected.

import { useMemo, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminCategories,
  useProductBrands,
  useCreateProductBrand,
  useDeactivateProductBrand,
  useUploadBrandImage,
} from '../hooks/useAdminCatalog.js';
import {
  useCreateCategory,
  useUpdateCategory,
  useDeactivateCategory,
  useDeleteCategory,
  useCategoryAttributes,
  useCreateCategoryAttribute,
  useUpdateCategoryAttribute,
  useDeactivateCategoryAttribute,
} from '../hooks/useAdminCategoryEditor.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import CategoryForm from '../components/CategoryForm.jsx';
import AttributeForm from '../components/AttributeForm.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { ChevronRight } from 'lucide-react';
import styles from './CategoriesPage.module.css';

const CATEGORY_CODES = [
  'CATEGORY_NOT_FOUND',
  'CATEGORY_HAS_DEPENDENTS',
  'CATEGORY_ALREADY_INACTIVE',
  'VALIDATION_ERROR',
];
const ATTR_CODES = [
  'CATEGORY_NOT_FOUND',
  'CATEGORY_ATTRIBUTE_NOT_FOUND',
  'CATEGORY_ATTRIBUTE_ALREADY_INACTIVE',
  'DUPLICATE_ATTRIBUTE_KEY',
  'VALIDATION_ERROR',
];
const BRAND_CODES = [
  'DUPLICATE_BRAND_NAME',
  'PRODUCT_BRAND_NOT_FOUND',
  'PRODUCT_BRAND_ALREADY_INACTIVE',
  'VALIDATION_ERROR',
];

// Mirrors the server's own limits (handleBrandImageUpload in
// routes/adminCatalogRoutes.js) — same client-side-only fast-feedback
// relationship as ProductForm.jsx's own ACCEPTED_IMAGE_TYPES/MAX_IMAGE_BYTES,
// duplicated here rather than imported since this is a page, not a shared
// upload component.
const ACCEPTED_BRAND_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BRAND_IMAGE_BYTES = 8 * 1024 * 1024;

// Flat list -> id -> [children], each list sorted by sort_order then name.
function buildTree(categories) {
  const byParent = new Map();
  for (const c of categories) {
    const key = c.parent_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }
  return byParent;
}

// The ids that can't be a new parent for `category` — itself and everything
// under it (a category may not become its own descendant).
function descendantIdsInclusive(byParent, id) {
  const out = new Set([id]);
  const walk = (pid) => {
    for (const child of byParent.get(pid) || []) {
      out.add(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

function TreeNode({ node, byParent, depth, selectedId, onSelect }) {
  const { t, lang } = useLanguage();
  const children = byParent.get(node.id) || [];
  const name = lang === 'am' && node.name_am ? node.name_am : node.name;
  return (
    <>
      <button
        type="button"
        className={`${styles.treeItem} ${selectedId === node.id ? styles.treeItemActive : ''} ${
          node.is_active ? '' : styles.treeItemInactive
        }`}
        style={{ paddingLeft: `calc(var(--space-2) + ${depth} * var(--space-3))` }}
        onClick={() => onSelect(node.id)}
      >
        {children.length > 0 ? (
          <ChevronRight size={14} aria-hidden="true" className={styles.treeChevron} />
        ) : (
          <span className={styles.treeBullet} aria-hidden="true" />
        )}
        <span className={styles.treeName}>{name}</span>
        {!node.is_active && (
          <span className={styles.treeInactiveTag}>{t('categories.inactiveTag')}</span>
        )}
      </button>
      {children.map((child) => (
        <TreeNode
          key={child.id}
          node={child}
          byParent={byParent}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function AttributeRow({ attribute, categoryId, onEdit }) {
  const { t } = useLanguage();
  const deactivateMutation = useDeactivateCategoryAttribute(categoryId);
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const handleDeactivate = async () => {
    const ok = await confirm({
      title: t('categories.attr.deactivateConfirmTitle', { name: attribute.label }),
      message: t('categories.attr.deactivateConfirmBody'),
      confirmLabel: t('categories.attr.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(attribute.id, {
      onSuccess: () => toastOk(t('categories.attr.deactivatedToast')),
      onError: (err) => toastErr(err, ATTR_CODES),
    });
  };

  return (
    <tr className={`${styles.attrRow} ${attribute.is_active ? '' : styles.attrRowInactive}`}>
      <td>
        <span className={styles.attrLabel}>{attribute.label}</span>
        <span className={styles.attrKey}>{attribute.attribute_key}</span>
      </td>
      <td>{t(`categories.attr.inputType.${attribute.input_type}`)}</td>
      <td className="tabularNums">{attribute.sort_order}</td>
      <td>
        {attribute.required ? t('categories.attr.requiredYes') : t('categories.attr.requiredNo')}
      </td>
      <td className={styles.attrActions}>
        {attribute.is_active ? (
          <>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => onEdit(attribute.id)}
            >
              {t('categories.attr.edit')}
            </button>
            <button
              type="button"
              className={styles.deactivateLink}
              onClick={handleDeactivate}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending && <Spinner />} {t('categories.attr.deactivate')}
            </button>
          </>
        ) : (
          <span className={styles.mutedSmall}>{t('categories.inactiveTag')}</span>
        )}
      </td>
    </tr>
  );
}

function AttributesEditor({ categoryId }) {
  const { t } = useLanguage();
  const {
    data: attributes,
    isLoading,
    isError,
    error,
    refetch,
  } = useCategoryAttributes(categoryId);
  const createMutation = useCreateCategoryAttribute(categoryId);
  const updateMutation = useUpdateCategoryAttribute(categoryId);
  const { toastOk, toastErr } = useActionFeedback();
  const [formMode, setFormMode] = useState(null); // null | 'create' | id

  const list = attributes || [];
  const editing = typeof formMode === 'number' ? list.find((a) => a.id === formMode) : null;

  const handleCreate = (body) => {
    createMutation.mutate(body, {
      onSuccess: () => {
        setFormMode(null);
        toastOk(t('categories.attr.createdToast'));
      },
      onError: (err) => toastErr(err, ATTR_CODES),
    });
  };

  const handleEdit = (body) => {
    updateMutation.mutate(
      { attributeId: formMode, patch: body },
      {
        onSuccess: () => {
          setFormMode(null);
          toastOk(t('categories.attr.updatedToast'));
        },
        onError: (err) => toastErr(err, ATTR_CODES),
      }
    );
  };

  return (
    <section className={styles.attrSection}>
      <div className={styles.attrHeader}>
        <h3 className={styles.sectionTitle}>{t('categories.attr.title')}</h3>
        <button
          type="button"
          className={styles.newButton}
          onClick={() => setFormMode((prev) => (prev === 'create' ? null : 'create'))}
        >
          {t('categories.attr.newAttribute')}
        </button>
      </div>

      {formMode === 'create' && (
        <AttributeForm
          onSubmit={handleCreate}
          onCancel={() => setFormMode(null)}
          isPending={createMutation.isPending}
        />
      )}
      {editing && (
        <AttributeForm
          key={editing.id}
          initialValues={editing}
          onSubmit={handleEdit}
          onCancel={() => setFormMode(null)}
          isPending={updateMutation.isPending}
        />
      )}

      {isLoading && <LoadingState variant="rows" rows={3} />}
      {isError && !isLoading && (
        <ErrorState messageId="categories.attr.errorBody" error={error} onRetry={refetch} />
      )}
      {!isLoading && !isError && list.length === 0 && (
        <p className={styles.muted}>{t('categories.attr.empty')}</p>
      )}
      {!isLoading && !isError && list.length > 0 && (
        <table className={styles.attrTable}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('categories.attr.col.label')}</th>
              <th>{t('categories.attr.col.type')}</th>
              <th>{t('categories.attr.col.order')}</th>
              <th>{t('categories.attr.col.required')}</th>
              <th>{t('categories.attr.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((attribute) => (
              <AttributeRow
                key={attribute.id}
                attribute={attribute}
                categoryId={categoryId}
                onEdit={setFormMode}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CategoryDetail({ category, allCategories, byParent, onDeselect }) {
  const { t, lang } = useLanguage();
  const primaryName = lang === 'am' && category.name_am ? category.name_am : category.name;
  const secondaryName = primaryName === category.name ? category.name_am : category.name;
  const updateMutation = useUpdateCategory();
  const deactivateMutation = useDeactivateCategory();
  const deleteMutation = useDeleteCategory();
  const { confirm, toastOk, toastErr } = useActionFeedback();
  const [editing, setEditing] = useState(false);

  const blockedParentIds = useMemo(
    () => descendantIdsInclusive(byParent, category.id),
    [byParent, category.id]
  );
  const parentOptions = allCategories.filter((c) => c.is_active && !blockedParentIds.has(c.id));

  const handleEdit = (patch) => {
    updateMutation.mutate(
      { id: category.id, patch },
      {
        onSuccess: () => {
          setEditing(false);
          toastOk(t('categories.updatedToast'));
        },
        onError: (err) => toastErr(err, CATEGORY_CODES),
      }
    );
  };

  const handleDeactivate = async () => {
    const ok = await confirm({
      title: t('categories.deactivateConfirmTitle', { name: primaryName }),
      message: t('categories.deactivateConfirmBody'),
      confirmLabel: t('categories.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(category.id, {
      onSuccess: () => toastOk(t('categories.deactivatedToast')),
      onError: (err) => toastErr(err, CATEGORY_CODES),
    });
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('categories.deleteConfirmTitle', { name: primaryName }),
      message: t('categories.deleteConfirmBody'),
      confirmLabel: t('categories.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(category.id, {
      onSuccess: () => {
        onDeselect();
        toastOk(t('categories.deletedToast'));
      },
      onError: (err) => toastErr(err, CATEGORY_CODES),
    });
  };

  return (
    <div className={styles.detail}>
      {editing ? (
        <CategoryForm
          initialValues={category}
          parentOptions={parentOptions}
          onSubmit={handleEdit}
          onCancel={() => setEditing(false)}
          isPending={updateMutation.isPending}
        />
      ) : (
        <div className={styles.detailHead}>
          <div>
            <h2 className={styles.detailName}>{primaryName}</h2>
            {secondaryName && <p className={styles.detailNameAm}>{secondaryName}</p>}
            {!category.is_active && (
              <p className={styles.detailInactive}>{t('categories.detailInactiveNote')}</p>
            )}
          </div>
          <div className={styles.detailActions}>
            <button type="button" className={styles.linkButton} onClick={() => setEditing(true)}>
              {t('categories.editDetails')}
            </button>
            {category.is_active && (
              <button
                type="button"
                className={styles.deactivateLink}
                onClick={handleDeactivate}
                disabled={deactivateMutation.isPending}
              >
                {deactivateMutation.isPending && <Spinner />} {t('categories.deactivate')}
              </button>
            )}
            <button
              type="button"
              className={styles.deactivateLink}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Spinner />} {t('categories.delete')}
            </button>
          </div>
        </div>
      )}

      <hr className="hairline" />

      <AttributesEditor categoryId={category.id} />
    </div>
  );
}

// One brand row's photo cell: thumbnail (brand.image_url, else a neutral
// placeholder box — this table is otherwise text-only/ledger-style, root
// CLAUDE.md's design section, so no card chrome here) plus a file input +
// upload button. Adapts ProductForm.jsx's own photoRow/photoControls/
// photoFileInput/photoUploadButton pattern (accepted-types/size-limit
// client check, pending/error state) to a table cell instead of a form
// section — same interaction, smaller footprint.
function BrandPhotoCell({ brand }) {
  const { t } = useLanguage();
  const uploadMutation = useUploadBrandImage();
  const { toastOk, toastErr } = useActionFeedback();
  const [file, setFile] = useState(null);
  const [errorId, setErrorId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const picked = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setErrorId(null);
    if (!picked) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_BRAND_IMAGE_TYPES.has(picked.type)) {
      setErrorId('categories.brands.photoInvalidType');
      setFile(null);
      e.target.value = '';
      return;
    }
    if (picked.size > MAX_BRAND_IMAGE_BYTES) {
      setErrorId('categories.brands.photoTooLarge');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(picked);
  };

  const handleUpload = () => {
    if (!file) return;
    setErrorId(null);
    uploadMutation.mutate(
      { id: brand.id, file },
      {
        onSuccess: () => {
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          toastOk(t('categories.brands.photoUploadedToast'));
        },
        onError: (err) => toastErr(err, BRAND_CODES),
      }
    );
  };

  return (
    <td className={styles.brandPhotoCell}>
      {brand.image_url ? (
        <img
          src={brand.image_url}
          alt={t('categories.brands.photoAltText')}
          className={styles.brandThumb}
        />
      ) : (
        <div className={styles.brandThumbPlaceholder} aria-hidden="true" />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className={styles.brandPhotoFileInput}
      />
      <button
        type="button"
        className={styles.linkButton}
        onClick={handleUpload}
        disabled={!file || uploadMutation.isPending}
      >
        {uploadMutation.isPending && <Spinner />}{' '}
        {uploadMutation.isPending
          ? t('categories.brands.photoUploading')
          : t('categories.brands.photoUpload')}
      </button>
      {errorId && <span className={styles.brandError}>{t(errorId)}</span>}
    </td>
  );
}

// Flat, admin-managed brand-name list (migration 027/028, table
// product_brands) — NOT scoped to a category, unlike AttributesEditor
// above, so this is its own top-level section rather than living inside
// CategoryDetail. Visible regardless of which category (if any) is
// selected. includeInactive: true so this management list also shows
// deactivated brands, dimmed (same treatment as AttributeRow's
// attrRowInactive) — the active-only variant stays reserved for
// ProductForm's own dropdown (hooks/useAdminCatalog.js's own comment).
function ProductBrandsSection() {
  const { t } = useLanguage();
  const {
    data: brands,
    isLoading,
    isError,
    error,
    refetch,
  } = useProductBrands({ includeInactive: true });
  const createMutation = useCreateProductBrand();
  const deactivateMutation = useDeactivateProductBrand();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [clientErrorId, setClientErrorId] = useState(null);

  const list = brands || [];

  const handleAddSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);
    if (!newName.trim()) {
      setClientErrorId('categories.brands.nameRequired');
      return;
    }
    createMutation.mutate(
      { name: newName.trim() },
      {
        onSuccess: () => {
          setAdding(false);
          setNewName('');
          toastOk(t('categories.brands.createdToast'));
        },
        onError: (err) => toastErr(err, BRAND_CODES),
      }
    );
  };

  const handleDeactivate = async (brand) => {
    const ok = await confirm({
      title: t('categories.brands.deactivateConfirmTitle', { name: brand.name }),
      message: t('categories.brands.deactivateConfirmBody'),
      confirmLabel: t('categories.brands.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(brand.id, {
      onSuccess: () => toastOk(t('categories.brands.deactivatedToast')),
      onError: (err) => toastErr(err, BRAND_CODES),
    });
  };

  return (
    <section className={styles.brandsSection}>
      <div className={styles.attrHeader}>
        <div>
          <h2 className={styles.sectionTitle}>{t('categories.brands.title')}</h2>
          <p className={styles.muted}>{t('categories.brands.subtitle')}</p>
        </div>
        <button
          type="button"
          className={styles.newButton}
          onClick={() => setAdding((prev) => !prev)}
        >
          {t('categories.brands.newBrand')}
        </button>
      </div>

      {adding && (
        <form className={styles.brandAddForm} onSubmit={handleAddSubmit}>
          <label className={styles.brandField}>
            {t('categories.brands.nameLabel')}
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              maxLength={80}
              className={styles.brandInput}
            />
          </label>
          <button type="submit" className={styles.linkButton} disabled={createMutation.isPending}>
            {createMutation.isPending && <Spinner />}{' '}
            {createMutation.isPending ? t('categories.brands.adding') : t('categories.brands.add')}
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setAdding(false);
              setNewName('');
              setClientErrorId(null);
            }}
          >
            {t('categories.brands.cancel')}
          </button>
          {clientErrorId && <span className={styles.brandError}>{t(clientErrorId)}</span>}
        </form>
      )}

      {isLoading && <LoadingState variant="rows" rows={3} />}
      {isError && !isLoading && (
        <ErrorState messageId="categories.brands.errorBody" error={error} onRetry={refetch} />
      )}
      {!isLoading && !isError && list.length === 0 && (
        <EmptyState titleId="categories.brands.empty" bodyId="categories.brands.emptyBody" />
      )}
      {!isLoading && !isError && list.length > 0 && (
        <table className={styles.attrTable}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('categories.brands.col.name')}</th>
              <th>{t('categories.brands.col.photo')}</th>
              <th>{t('categories.brands.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((brand) => (
              <tr
                key={brand.id}
                className={`${styles.attrRow} ${brand.is_active ? '' : styles.attrRowInactive}`}
              >
                <td>{brand.name}</td>
                <BrandPhotoCell brand={brand} />
                <td className={styles.attrActions}>
                  {brand.is_active ? (
                    <button
                      type="button"
                      className={styles.deactivateLink}
                      onClick={() => handleDeactivate(brand)}
                      disabled={deactivateMutation.isPending}
                    >
                      {deactivateMutation.isPending && <Spinner />}{' '}
                      {t('categories.brands.deactivate')}
                    </button>
                  ) : (
                    <span className={styles.mutedSmall}>{t('categories.inactiveTag')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function CategoriesPage() {
  const { t } = useLanguage();
  const { data: categories, isLoading, isError, error, refetch } = useAdminCategories();
  const createMutation = useCreateCategory();
  const { toastOk, toastErr } = useActionFeedback();

  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const all = categories || [];
  const byParent = useMemo(() => buildTree(all), [all]);
  const roots = byParent.get(null) || [];
  const selected = all.find((c) => c.id === selectedId) || null;

  const handleCreate = (body) => {
    createMutation.mutate(body, {
      onSuccess: (data) => {
        setCreating(false);
        if (data?.category?.id) setSelectedId(data.category.id);
        toastOk(t('categories.createdToast'));
      },
      onError: (err) => toastErr(err, CATEGORY_CODES),
    });
  };

  if (isLoading) return <LoadingState variant="page" />;
  if (isError)
    return <ErrorState messageId="categories.errorBody" error={error} onRetry={refetch} />;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('categories.title')}</h1>
      <p className={styles.subtitle}>{t('categories.subtitle')}</p>

      <div className={styles.layout}>
        <aside className={styles.treePanel}>
          <button
            type="button"
            className={styles.newButton}
            onClick={() => setCreating((prev) => !prev)}
          >
            {t('categories.newCategory')}
          </button>

          {creating && (
            <CategoryForm
              parentOptions={all.filter((c) => c.is_active)}
              onSubmit={handleCreate}
              onCancel={() => setCreating(false)}
              isPending={createMutation.isPending}
            />
          )}

          {all.length === 0 ? (
            <EmptyState titleId="categories.emptyTitle" bodyId="categories.emptyBody" />
          ) : (
            <div className={styles.tree}>
              {roots.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  byParent={byParent}
                  depth={0}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              ))}
            </div>
          )}
        </aside>

        <div className={styles.detailPanel}>
          {selected ? (
            <CategoryDetail
              key={selected.id}
              category={selected}
              allCategories={all}
              byParent={byParent}
              onDeselect={() => setSelectedId(null)}
            />
          ) : (
            <p className={styles.muted}>{t('categories.selectPrompt')}</p>
          )}
        </div>
      </div>

      <hr className="hairline" />

      <ProductBrandsSection />
    </div>
  );
}
